import React, { useMemo } from 'react';
import { Redirect, Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import isIncludes from 'modules/AccessControl/isIncludes';
import { AuthEntity } from '_entities';
import { useSelector } from 'react-redux';

const { getProfilePermissions, getModularityPermissions, getUserRoleType, getIsSuperuser } = AuthEntity.selectors;
function PrivateRoute({ path, component, permission, isAllAllow, isVisit, allowedRoleTypes, allowSuperuser, ...rest }) {
  const permissions = useSelector(getProfilePermissions);
  const modularityPermissions = useSelector(getModularityPermissions);
  const userRoleType = useSelector(getUserRoleType);
  const isSuperuser = useSelector(getIsSuperuser);
  const userAndModularityPermissions = permissions?.concat(modularityPermissions);

  const hasRights = useMemo(() => {
    if (allowedRoleTypes) {
      return allowedRoleTypes.includes(userRoleType);
    }
    // P20M-1309 — superusers pass every permission on the backend; mirror that
    // here so a superuser reaches a permission-gated route (e.g. the App
    // Dashboard) even when the permission is not assigned to their role.
    if (allowSuperuser && isSuperuser) {
      return true;
    }
    return isAllAllow || isIncludes(userAndModularityPermissions, permission, isVisit);
  }, [
    isAllAllow,
    permission,
    userAndModularityPermissions,
    isVisit,
    allowedRoleTypes,
    userRoleType,
    allowSuperuser,
    isSuperuser,
  ]);

  // The "already authenticated, redirect away from /login" case used to be attempted here, but a
  // PrivateRoute never renders on /login — Switch picks its child by the `path` prop, and no private
  // route owns that path. It lives in PublicOnlyRoute now, on the side of the Switch that does match.
  return hasRights ? <Route path={path} component={component} {...rest} /> : <Redirect to="/" />;
}

PrivateRoute.propTypes = {
  // eslint-disable-next-line react/require-default-props
  path: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.array.isRequired]),
  permission: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isAllAllow: PropTypes.bool,
  component: PropTypes.elementType.isRequired,
  isVisit: PropTypes.bool,
  allowedRoleTypes: PropTypes.arrayOf(PropTypes.number),
  allowSuperuser: PropTypes.bool,
};

PrivateRoute.defaultProps = {
  permission: '',
  isAllAllow: false,
  isVisit: false,
  allowedRoleTypes: null,
  allowSuperuser: false,
};

export default PrivateRoute;
